/**
 * UC-CUSTOM (2026-06-05): Variant-specific descriptions
 *
 * Replaces app "Variant Descriptions King" (StarApps) — hashtag system only,
 * NO accordion functionality.
 *
 * How it works:
 *   In product description, sections marked with `<h2>#VariantName</h2>` show
 *   only when that variant is selected. Hashtags supported:
 *     - #VariantName        — single option match
 *     - #Red #Blue          — multiple variants (one match = show)
 *     - #MensTshirt-Green   — multi-option (both must match)
 *     - #All                — always visible
 *
 * Normalization: lowercase + keep only Unicode letters/digits (strips spaces,
 * dashes, slashes, punctuation). Romanian diacritics ă î â ș ț preserved.
 *
 * Canopy v7.3 specifics (discovered via diagnostic 2026-06-05):
 *   - Variant inputs: name ends in "-option" (NOT "options[Name]")
 *   - Selected variant ID: input[name="id"] changes on selection
 *   - NO custom event dispatched (no variant:change); use native change events
 *
 * Debug: set DEBUG_FORCE = true to log to console (default off after stable).
 */
(function () {
  'use strict';

  var DEBUG_FORCE = false; // Set to true to enable console logging during debug
  function log() {
    if (!DEBUG_FORCE && !window.UC_VARIANT_DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[UC-Variant]');
    console.log.apply(console, args);
  }

  function normalize(str) {
    return String(str).toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
  }

  // Find currently-selected option values. Canopy uses inputs with name ending in "-option".
  function getCurrentOptions() {
    var options = [];

    // Primary: Canopy v7.3 — input[name$="-option"]:checked (radio buttons)
    document.querySelectorAll('input[name$="-option"]:checked').forEach(function (i) {
      if (i.value) options.push(i.value);
    });

    // Fallback 1: standard Shopify pattern (other themes)
    if (options.length === 0) {
      document.querySelectorAll('form[action*="/cart/add"] input[name^="options["]:checked, form[action*="/cart/add"] select[name^="options["]').forEach(function (i) {
        if (i.value) options.push(i.value);
      });
    }

    // Fallback 2: any select inside variant-picker
    if (options.length === 0) {
      document.querySelectorAll('variant-picker select').forEach(function (s) {
        if (s.value) options.push(s.value);
      });
    }

    return Array.from(new Set(options.filter(Boolean)));
  }

  function findDescriptions() {
    return document.querySelectorAll(
      '.product-description.rte, ' +
      '[class*="main-product-description"] .rte, ' +
      '.product__description .rte, ' +
      '[data-product-description] .rte'
    );
  }

  /**
   * Parsează markeri de variantă din descriere.
   *
   * 2026-06: format NOU = HTML comment `<!-- variant: X -->` (SEO-friendly, nu inflează headings)
   * Legacy = `<h1-6>#X</h1-6>` — încă suportat pentru tranziție lină (poate fi eliminat după
   * ce toate descrierile Milwaukee au fost migrate via Hextom).
   */
  function parseSections(descEl) {
    var sections = [];

    // Walk all child nodes (include comments alongside elements)
    var nodes = Array.from(descEl.childNodes);

    nodes.forEach(function (node) {
      var markerText = null;
      var isLegacyHeading = false;

      // 1. FORMAT NOU: HTML comment
      if (node.nodeType === Node.COMMENT_NODE) {
        var commentMatch = node.data.trim().match(/^variant:\s*(.+)$/i);
        if (commentMatch) markerText = '#' + commentMatch[1].trim();
      }
      // 2. LEGACY: <h1-6>#X</h1-6>
      else if (node.nodeType === Node.ELEMENT_NODE && /^H[1-6]$/.test(node.tagName)) {
        var text = node.textContent.trim();
        if (text.charAt(0) === '#') {
          markerText = text;
          isLegacyHeading = true;
        }
      }

      if (markerText === null) return;

      var matches = markerText.match(/#[^\s#]+/g);
      if (!matches) return;
      var tags = matches.map(function (t) { return normalize(t.substring(1)); });

      // Conținutul = următoarele element nodes până la următorul marker (de orice tip)
      var content = [];
      var sibling = node.nextSibling;
      while (sibling) {
        // Stop la următorul marker (comment "variant:" SAU heading cu #)
        if (sibling.nodeType === Node.COMMENT_NODE && /^variant:/i.test(sibling.data.trim())) break;
        if (sibling.nodeType === Node.ELEMENT_NODE && /^H[1-6]$/.test(sibling.tagName) && sibling.textContent.trim().charAt(0) === '#') break;

        if (sibling.nodeType === Node.ELEMENT_NODE) content.push(sibling);
        sibling = sibling.nextSibling;
      }

      // Ascunde markerul legacy h2 (comentariile sunt deja invizibile)
      if (isLegacyHeading) node.style.display = 'none';

      sections.push({ tags: tags, content: content });
    });

    return sections;
  }

  function update(sections) {
    var current = getCurrentOptions();
    var currentTags = current.map(normalize);
    if (current.length > 1) currentTags.push(current.map(normalize).join('-'));

    log('Current options:', current, '→ tags:', currentTags);

    var anyShown = false;
    sections.forEach(function (s) {
      var show = s.tags.indexOf('all') !== -1 ||
                 s.tags.some(function (t) { return currentTags.indexOf(t) !== -1; });
      if (show) anyShown = true;
      s.content.forEach(function (el) { el.style.display = show ? '' : 'none'; });
    });

    // Safety: if no section matched, show all (better than blank)
    if (!anyShown) {
      log('No match — fallback: showing all');
      sections.forEach(function (s) {
        s.content.forEach(function (el) { el.style.display = ''; });
      });
    }
  }

  function init() {
    var descs = findDescriptions();
    if (descs.length === 0) return;

    descs.forEach(function (desc) {
      var sections = parseSections(desc);
      if (sections.length === 0) return;

      // Brief FOUC prevention
      desc.style.transition = 'opacity 0.15s';
      desc.style.opacity = '0';
      update(sections);
      requestAnimationFrame(function () { desc.style.opacity = ''; });

      // Listen for ANY change on variant inputs (Canopy v7.3 native pattern)
      document.addEventListener('change', function (e) {
        if (!e.target || !e.target.name) return;
        var name = e.target.name;
        // Match: name ends in "-option" (Canopy), OR name="id" (variant ID changed), OR name starts with "options["
        if (name.endsWith('-option') || name === 'id' || name.indexOf('options[') === 0) {
          log('Variant change detected via:', name, '=', e.target.value);
          update(sections);
        }
      });

      // Fallback: also listen for variant:change (other themes)
      document.addEventListener('variant:change', function () {
        log('variant:change event fired');
        update(sections);
      });
    });
  }

  log('uc-variant-descriptions.js loaded');
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
